import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  Layers,
  Zap,
  Code,
  Clock,
  Calendar,
  Puzzle,
} from 'lucide-react';

function Plugins() {
  const { t } = useTranslation();
  const { lang } = useParams<{ lang: string }>();

  return (
    <>
      <Helmet>
        <title>Plugins & Integrations - ShiftPic</title>
        <meta name="description" content="Enhance your workflow with ShiftPic plugins and integrations. Convert images directly in your favorite design tools and applications." />
        <meta name="keywords" content="ShiftPic plugins, Figma plugin, image converter integration, design tools, workflow automation, image optimization plugin, Figma image converter, batch image conversion, design workflow tools" />
        <link rel="canonical" href={`https://shiftpic.com/${lang || 'en'}/plugins/`} />
        <meta property="og:title" content="Plugins & Integrations - ShiftPic" />
        <meta property="og:description" content="Enhance your workflow with ShiftPic plugins and integrations. Convert images directly in your favorite design tools and applications." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://shiftpic.com/${lang || 'en'}/plugins/`} />
        <meta property="og:image" content="https://shiftpic.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Plugins & Integrations - ShiftPic" />
        <meta name="twitter:description" content="Enhance your workflow with ShiftPic plugins and integrations. Convert images directly in your favorite design tools and applications." />
        <meta name="twitter:image" content="https://shiftpic.com/og-image.jpg" />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
          <div className="max-w-[800px] mt-8 mb-16">
            <h1>{t('plugins.title')}</h1>
            <p>{t('plugins.description')}</p>
          </div>

          {/* Coming Soon - Figma Plugin */}
          <div className="bg-white rounded-card shadow-lg overflow-hidden mb-12">
            <div className="p-8">
              <div className="flex items-center mb-6">
                <div>
                  <div className="flex items-center">
                    <h2 className="mb-1 mt-0">
                      {t('plugins.figma.title')}
                    </h2>
                    <span className="ml-3 bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full">
                      {t('plugins.figma.status')}
                    </span>
                  </div>
                  <p className="mt-0.5">{t('plugins.figma.subtitle')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="mb-6">{t('plugins.figma.description')}</p>

                  <h4>
                    {t('plugins.figma.features.title')}
                  </h4>
                  <ul className="space-y-3 mb-6 pl-0">
                    <li className="flex items-start">
                      <div
                        className="mr-2 mt-1 flex-shrink-0"
                        style={{ width: '24px', height: '24px' }}
                      >
                        <Layers className="process-icon" size={16} />
                      </div>
                      <span>{t('plugins.figma.features.batch')}</span>
                    </li>
                    <li className="flex items-start">
                      <div
                        className="mr-2 mt-1 flex-shrink-0"
                        style={{ width: '24px', height: '24px' }}
                      >
                        <Zap className="process-icon" size={16} />
                      </div>
                      <span>{t('plugins.figma.features.optimize')}</span>
                    </li>
                    <li className="flex items-start">
                      <div
                        className="mr-2 mt-1 flex-shrink-0"
                        style={{ width: '24px', height: '24px' }}
                      >
                        <Code className="process-icon" size={16} />
                      </div>
                      <span>{t('plugins.figma.features.export')}</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-xl overflow-hidden">
                  <img
                    src="/images/figma_plugin_1x.webp"
                    srcSet="/images/figma_plugin_1x.webp 600w, /images/figma_plugin_2x.webp 1200w"
                    sizes="(max-width: 768px) 100vw, 50vw"
                    alt="ShiftPic Figma Plugin Preview"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h4>
                  {t('plugins.figma.timeline.title')}
                </h4>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="bg-gray-100 p-4 rounded-xl border border-gray-400 flex-1">
                    <div className="flex items-center mb-2">
                      <Clock className="text-primary mr-2" size={18} />
                      <h5 className="mb-1 mt-2">
                        {t('plugins.figma.timeline.beta.title')}
                      </h5>
                    </div>
                    <p className="text-gray-600 text-sm">
                      {t('plugins.figma.timeline.beta.date')}
                    </p>
                  </div>
                  <div className="bg-gray-100 p-4 rounded-xl border border-gray-400 flex-1">
                    <div className="flex items-center mb-2">
                      <Calendar className="text-primary mr-2" size={18} />
                      <h5 className="mb-1 mt-2">
                        {t('plugins.figma.timeline.public.title')}
                      </h5>
                    </div>
                    <p className="text-gray-700 text-sm">
                      {t('plugins.figma.timeline.public.date')}
                    </p>
                  </div>
                  <div className="bg-gray-100 p-4 rounded-xl border border-gray-400 flex-1">
                    <div className="flex items-center mb-2">
                      <Puzzle className="text-primary mr-2" size={18} />
                      <h5 className="mb-1 mt-2">
                        {t('plugins.figma.timeline.updates.title')}
                      </h5>
                    </div>
                    <p className="text-gray-700 text-sm">
                      {t('plugins.figma.timeline.updates.schedule')}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
    </>
  );
}

export default Plugins;